import { Button } from "@/components/flexkit/Button";
import { Pill } from "@/components/podkit/status/Pill";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { Text as Paragraph } from "@/components/podkit/typography/Text";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { EducationPageContainer } from "@/components/education/EducationPageContainer";
import { AutomationTemplateGroup } from "@/components/automations/AutomationTemplateGroup";
import { ChangeSubscriptionModal } from "@/routes/settings/organization/billing/ChangeSubscriptionModal";
import { getTemplateGroups } from "@/utils/automation-templates";
import { useFeatureAutomations } from "@/hooks/use-feature-automations";
import { type FC, useCallback, useMemo, useState } from "react";
import { ScrollArea } from "@/components/flexkit/ScrollArea";

export const AutomationsEducationPage: FC = () => {
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const { capabilities } = useFeatureAutomations();
    const groups = useMemo(() => getTemplateGroups(capabilities), [capabilities]);

    const handleUpgradeModalClose = useCallback((open: boolean) => {
        setShowUpgradeModal(open);
    }, []);

    return (
        // Scroll handling & padding needed as layout doesn't include padding
        <div className="size-full p-0.5">
            <ScrollArea orientation="vertical" className="flex max-h-full flex-col gap-6 p-6">
                <EducationPageContainer>
                    <div className="flex flex-col gap-2">
                        <Pill variant="brand" size="lg" className="w-fit">
                            Available on Core and above ✨
                        </Pill>
                        <Heading1>Build automations that scale across every repo.</Heading1>
                        <Paragraph className="text-md text-content-secondary">
                            Chain agents and commands into multi-step Automations that run across your entire codebase -
                            scheduled or event driven.
                        </Paragraph>

                        <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                            <Button
                                size="md"
                                onClick={() => setShowUpgradeModal(true)}
                                data-tracking-id="upgrade-to-core-automations-education-page"
                            >
                                Upgrade plan
                            </Button>
                            <Button variant="outline" size="md" asChild>
                                <ExternalLink href="https://ona.com/docs/ona/automations/overview" iconSize="sm">
                                    Learn more
                                </ExternalLink>
                            </Button>
                        </div>
                    </div>

                    <div className="flex flex-col gap-6">
                        {groups.map((group, index) => (
                            <AutomationTemplateGroup key={group.id} group={group} index={index} />
                        ))}
                    </div>

                    {showUpgradeModal && <ChangeSubscriptionModal onOpenChange={handleUpgradeModalClose} />}
                </EducationPageContainer>
            </ScrollArea>
        </div>
    );
};
